import { Upload } from "@mui/icons-material";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import React, { useState, useCallback } from "react";
import { useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { accountApi } from "../../../../services/accountService/account-api";
import { OutOfPeriodAdjustmentRow } from "../../../../services/accountService/model/account/OutOfPeriodAdjustment";
import { 
    loadRows, 
    resetUpload, 
    setPhase, 
    validateSingleRow, 
    uploadSingleRow 
} from "../../../../services/accountService/model/account/OpaUploadSlice";
import { OPAUploadGrid } from "./OPAUploadGrid";
import { OPAUploadSummary } from "./OPAUploadSummary";
import { OPATemplateDownload } from "./OPATemplateDownload";

function OPAImport() {
    const dispatch = useAppDispatch();
    const { center } = useParams();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | undefined>();
    const [parseError, setParseError] = useState<string | null>(null);

    const { rows, phase, centerId } = useAppSelector(state => state.opaUpload);

    const isProcessing = phase === 'validating' || phase === 'uploading';
    const canValidate = phase === 'parsing' && rows.length > 0;
    const hasErrors = rows.some(r => r.status === 'invalid');
    const hasValidRows = rows.some(r => r.status === 'valid' || r.status === 'valid-warning');
    const canUpload = phase === 'review' && hasValidRows && !hasErrors;
    const isComplete = phase === 'complete';

    // Clean amount string: remove $, commas, spaces, and handle accounting-style negatives (123.45) -> -123.45
    const cleanAmount = (value: string): string => {
        if (!value) return '';
        
        // Check for accounting-style negative: (123.45) or ( $1,234.56 )
        const trimmed = value.trim();
        const isAccountingNegative = trimmed.startsWith('(') && trimmed.endsWith(')');
        
        // Remove parentheses if accounting-style negative
        let working = isAccountingNegative ? trimmed.slice(1, -1) : trimmed;
        
        // Remove whitespace, dollar signs, and commas; keep digits, decimal point, and minus sign
        const cleaned = working.replace(/[\s$,]/g, '');
        
        // Validate it's a valid number format
        if (cleaned && !isNaN(parseFloat(cleaned))) {
            // Add negative sign if accounting-style negative
            return isAccountingNegative ? `-${cleaned}` : cleaned;
        }
        return value; // Return original if cleaning didn't produce valid number (let validation catch it)
    };

    // Parse CSV file client-side
    const parseCSV = (text: string): OutOfPeriodAdjustmentRow[] => {
        const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
        
        if (lines.length < 2) {
            throw new Error('CSV file must contain a header row and at least one data row.');
        }

        // Parse header
        const header = lines[0].split(',').map(h => h.trim().toLowerCase());
        const accountIdx = header.indexOf('accountnumber');
        const periodIdx = header.indexOf('period');
        const yearIdx = header.indexOf('year');
        const amountIdx = header.indexOf('amount');
        const reasonIdx = header.indexOf('reason');

        if (accountIdx === -1 || periodIdx === -1 || yearIdx === -1 || amountIdx === -1 || reasonIdx === -1) {
            throw new Error('CSV must contain columns: AccountNumber, Period, Year, Amount, Reason');
        }

        // Parse data rows
        const dataRows: OutOfPeriodAdjustmentRow[] = [];
        for (let i = 1; i < lines.length; i++) {
            const values = parseCSVLine(lines[i]);
            if (values.length > 0) {
                dataRows.push({
                    accountNumber: (values[accountIdx] || '').trim(),
                    period: (values[periodIdx] || '').trim(),
                    year: (values[yearIdx] || '').trim(),
                    amount: cleanAmount(values[amountIdx] || ''),
                    reason: (values[reasonIdx] || '').trim()
                });
            }
        }

        return dataRows;
    };

    // Handle CSV values with potential commas in quoted fields
    const parseCSVLine = (line: string): string[] => {
        const values: string[] = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
                inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
                values.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }
        values.push(current.trim());

        return values;
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        setSelectedFile(file);
        setParseError(null);

        if (file && center) {
            try {
                const text = await file.text();
                const parsedRows = parseCSV(text);
                
                if (parsedRows.length === 0) {
                    setParseError('No data rows found in file.');
                    return;
                }

                dispatch(loadRows({ centerId: center, rows: parsedRows }));
            } catch (error) {
                setParseError(error instanceof Error ? error.message : 'Failed to parse CSV file.');
            }
        }
    };

    const handleValidate = useCallback(async () => {
        if (!centerId || rows.length === 0) return;

        dispatch(setPhase('validating'));

        // Process each row sequentially
        for (const row of rows) {
            await dispatch(validateSingleRow({
                centerId,
                row: row.data,
                lineNumber: row.lineNumber
            })).unwrap().catch(() => {
                // Error handled by extraReducers
            });
        }

        dispatch(setPhase('review'));
    }, [dispatch, centerId, rows]);

    const handleUpload = useCallback(async () => {
        if (!centerId) return;

        dispatch(setPhase('uploading'));

        // Get valid and valid-warning rows for upload
        const validRows = rows.filter(r => r.status === 'valid' || r.status === 'valid-warning');

        // Process each valid row sequentially
        for (const row of validRows) {
            await dispatch(uploadSingleRow({
                centerId,
                row: row.data,
                lineNumber: row.lineNumber
            })).unwrap().catch(() => {
                // Error handled by extraReducers
            });
        }

        dispatch(setPhase('complete'));
    }, [dispatch, centerId, rows]);

    const handleClose = () => {
        // Prevent closing during processing
        if (isProcessing) {
            return;
        }

        // Invalidate cache to refresh data if any uploads succeeded
        const hasUploads = rows.some(r => r.status === 'uploaded');
        if (hasUploads) {
            dispatch(accountApi.util.invalidateTags(['AdjustmentSummary']));
        }

        // Reset state
        dispatch(resetUpload());
        setSelectedFile(undefined);
        setParseError(null);
        setIsOpen(false);
    };

    const handleDialogClose = (_event: object, reason: string) => {
        // Only allow closing via the Cancel/Close button, not backdrop or escape
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
        handleClose();
    };

    return (
        <React.Fragment>
            <Button variant='outlined' color='primary' onClick={() => setIsOpen(true)} startIcon={<Upload />}>
                Import Adjustments
            </Button>

            <Dialog 
                open={isOpen} 
                onClose={handleDialogClose} 
                maxWidth='lg' 
                fullWidth
                disableEscapeKeyDown={true}
            >
                <DialogTitle>Upload Out of Period Adjustments</DialogTitle>
                <DialogContent>
                    <Stack gap={2} marginTop='10px'>
                        <Alert severity='info'>
                            Upload a CSV file with columns: AccountNumber, Period, Year, Amount, Reason.<br />
                            Each row will be validated individually before upload. You can review results before confirming.
                            <Box marginTop={1}>
                                <OPATemplateDownload />
                            </Box>
                        </Alert>

                        <input 
                            type='file' 
                            name='file' 
                            accept='.csv' 
                            onChange={handleFileChange} 
                            disabled={isProcessing || isComplete} 
                        />

                        {parseError && (
                            <Alert severity='error'>{parseError}</Alert>
                        )}

                        {rows.length > 0 && (
                            <React.Fragment>
                                <OPAUploadSummary />
                                <OPAUploadGrid />
                            </React.Fragment>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleClose} 
                        color='secondary'
                        disabled={isProcessing}
                    >
                        {isComplete ? 'Close' : 'Cancel'}
                    </Button>
                    
                    {canValidate && (
                        <Button
                            onClick={handleValidate}
                            variant='contained'
                            color='primary'
                        >
                            Validate
                        </Button>
                    )}

                    {canUpload && (
                        <Button
                            onClick={handleUpload}
                            variant='contained'
                            color='success'
                        >
                            Upload Valid Rows
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

export { OPAImport };
